import { useState, useEffect } from 'react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SizeGuidePage() {
  const [activeCategory, setActiveCategory] = useState('men');
  const [sizeData, setSizeData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSizeGuide(activeCategory);
  }, [activeCategory]);

  const fetchSizeGuide = async (category) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/size-guide/${category}`);
      setSizeData(response.data);
    } catch (error) {
      console.error('Failed to fetch size guide:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderSizeTable = (subcategory, sizes) => {
    if (!sizes) return null;
    
    const sizeKeys = Object.keys(sizes);
    const measurements = Object.keys(sizes[sizeKeys[0]] || {});

    return (
      <div className="mb-8">
        <h3 className="font-['Syne'] text-lg font-bold mb-4 capitalize">{subcategory}</h3>
        <div className="border border-neutral-800 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-neutral-800">
                <TableHead className="text-white">Size</TableHead>
                {measurements.map((m) => (
                  <TableHead key={m} className="text-white capitalize">
                    {m.replace(/_/g, ' ')} (in)
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sizeKeys.map((size) => (
                <TableRow key={size} className="border-neutral-800">
                  <TableCell className="font-medium">{size}</TableCell>
                  {measurements.map((m) => (
                    <TableCell key={m}>{sizes[size][m]}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <div className="pt-24 md:pt-28 min-h-screen" data-testid="size-guide-page">
      <div className="container-custom max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-[#FF3B30] uppercase text-xs tracking-widest">Fit Guide</span>
          <h1 className="font-['Syne'] text-3xl md:text-5xl font-bold mt-2">Size Guide</h1>
          <p className="text-muted-foreground mt-4">
            Find your perfect fit. All measurements are in inches.
          </p>
        </div>

        {/* How to Measure */}
        <div className="border border-neutral-800 p-6 mb-12">
          <h2 className="font-['Syne'] text-xl font-bold mb-4">How to Measure</h2>
          <div className="grid md:grid-cols-3 gap-6 text-sm">
            <div>
              <h4 className="font-medium mb-2">Chest</h4>
              <p className="text-muted-foreground">
                Measure around the fullest part of your chest, keeping the tape horizontal.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2">Waist</h4>
              <p className="text-muted-foreground">
                Measure around your natural waistline, keeping the tape comfortably loose.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2">Hip</h4>
              <p className="text-muted-foreground">
                Measure around the fullest part of your hips, about 8" below your waist.
              </p>
            </div>
          </div>
        </div>

        {/* Size Charts */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
          <TabsList className="bg-neutral-900 mb-8 w-full justify-start flex-wrap h-auto">
            <TabsTrigger 
              value="men" 
              className="data-[state=active]:bg-white data-[state=active]:text-black rounded-none"
              data-testid="size-tab-men"
            >
              Men
            </TabsTrigger>
            <TabsTrigger 
              value="women"
              className="data-[state=active]:bg-white data-[state=active]:text-black rounded-none"
              data-testid="size-tab-women"
            >
              Women
            </TabsTrigger>
            <TabsTrigger 
              value="kids"
              className="data-[state=active]:bg-white data-[state=active]:text-black rounded-none"
              data-testid="size-tab-kids"
            >
              Kids
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="space-y-4">
              <div className="h-8 bg-neutral-900 animate-pulse w-32" />
              <div className="h-64 bg-neutral-900 animate-pulse" />
            </div>
          ) : (
            <>
              <TabsContent value="men">
                {sizeData?.sizes && (
                  <>
                    {renderSizeTable('tops', sizeData.sizes.tops)}
                    {renderSizeTable('bottoms', sizeData.sizes.bottoms)}
                  </>
                )}
              </TabsContent>

              <TabsContent value="women">
                {sizeData?.sizes && (
                  <>
                    {renderSizeTable('tops', sizeData.sizes.tops)}
                    {renderSizeTable('bottoms', sizeData.sizes.bottoms)}
                  </>
                )}
              </TabsContent>

              <TabsContent value="kids">
                {sizeData?.sizes && (
                  <>
                    {renderSizeTable('tops', sizeData.sizes.tops)}
                    {renderSizeTable('bottoms', sizeData.sizes.bottoms)}
                  </>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>

        {/* Tips */}
        <div className="border border-neutral-800 p-6 mt-12">
          <h2 className="font-['Syne'] text-xl font-bold mb-4">Fit Tips</h2>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li>• Our streetwear is designed with an oversized, relaxed fit. If you prefer a more fitted look, consider sizing down.</li>
            <li>• Hoodies and sweatshirts may shrink slightly after the first wash. We recommend washing in cold water.</li>
            <li>• When in doubt between two sizes, go with the larger size for that classic streetwear silhouette.</li>
            <li>• Kids' sizes are designed to accommodate growth, so they may run slightly larger than standard.</li>
          </ul>
        </div>

        {/* Contact */}
        <div className="text-center mt-12 text-sm text-muted-foreground">
          <p>Still unsure about your size? Contact us at support@lostnfound860.com</p>
        </div>
      </div>
    </div>
  );
}
